import { useLoginMutation, useTrackMutation } from '@mote/api'
import { getQueryParamsObject, useQueryParams } from '@mote/helpers'
import { LEGALS_VERSION } from '@mote/helpers/constants'
import { Spinner } from '@mote/ui'
import React from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useAuth } from '../../auth/useAuth'
import { AuthLayout } from '../../layouts'
import { postErrorEvent } from '../../shared/AnalyticsHelpers'
import { getOAuthURL, identifyMaybe } from '../../shared/Auth'
import { AuthForm } from './AuthForm'
import { LoginError } from './LoginError'
import { flattenDataFromResponse } from '@mote/helpers/identity/flattenDataFromResponse'

const RETURN_TO_WHITELIST = [
  'checkout',
  'checkout/unlimited',
  'support',
  'account',
  'admin',
  'admin/debugSAK',
  'gift',
  'g/',
  'm/',
  'onboarding',
  'onboarding/invitation'
]

export const AuthScreen = () => {
  const [error, setError] = React.useState(null)
  const params = useParams()
  const history = useHistory()
  const referrerCode = params.referrerCode
  const errorTimeout = React.useRef(null)

  const { user, updateUser, logout } = useAuth()

  const { pathname } = useLocation()
  const { queryParams, cleanQueryParams } = useQueryParams()
  const [loading, setLoading] = React.useState(queryParams.code)

  const { mutate: track } = useTrackMutation()
  const { mutate: login } = useLoginMutation()

  const isLoginPage = React.useMemo(() => pathname === '/login', [pathname])

  const screenData = React.useMemo(() => {
    if (isLoginPage)
      return {
        title: 'Sign in to Mote',
        subtitle:
          'Mote is voice messaging for the web, integrated into the products you already know and love.',
        caption: 'Dont have an account?',
        isSignupPage: false,
        cta: {
          text: 'Sign in with Google',
          action: startOauth
        },
        redirect: {
          text: 'Sign up free',
          urlPath: '/signup'
        }
      }

    return {
      title: 'Sign up to Mote',
      subtitle:
        'Start with a 30-day trial of Mote Unlimited. After, either upgrade for full product access, or continue on our free plan.',
      caption: 'Already have an account?',
      isSignupPage: true,
      cta: {
        text: 'Sign up with Google',
        action: startOauth
      },
      redirect: {
        text: 'Sign in',
        urlPath: '/login'
      }
    }
  }, [isLoginPage])

  React.useEffect(() => {
    if (!user) return
    // TODO remove in few months - change added 9/9/21
    // queryParams.addScope is for backwards compatibility
    if (queryParams.force || referrerCode || queryParams.addScope) return

    history.push('/onboarding')
  }, [])

  React.useEffect(() => {
    if (Object.keys(queryParams).length === 0 && !referrerCode) return

    if (referrerCode && user) {
      return history.push('/')
    }
    // TODO remove in few months - change added 9/9/21
    // queryParams.addScope is for backwards compatibility
    if ((queryParams.force || queryParams.addScope) && user) {
      handleLogout(
        Boolean(queryParams.force),
        queryParams.invoker === 'extension'
      )

      return
    }

    if (!queryParams.code) return
    if (user) return

    track({
      variables: {
        event: 'Authentication: Returning from Google OAuth',
        withScopes: 1
      }
    })

    errorTimeout.current = setTimeout(() => {
      track({ variables: { event: 'Authentication: Login Error' } })
      setError('We could not log you in after 15 seconds.')
    }, 5000)

    const paramsFromState = getQueryParamsObject(queryParams.state)
    if (paramsFromState.returnTo) {
      delete paramsFromState.returnTo
    }

    const { code, ...restQueryParams } = queryParams

    login({
      variables: {
        authCode: code,
        ...restQueryParams,
        ...paramsFromState
      }
    }).then(res => {
      if (res.error) {
        return loginFailureCallback(res.error)
      }
      return loginSuccessCallback(flattenDataFromResponse(res.data))
    })
  }, [referrerCode, user])

  async function loginSuccessCallback(user) {
    await track({ variables: { event: 'Login Completed' } })
    clearErrorTimeout()
    updateUser(user)
    identifyMaybe()

    if (!queryParams.state.includes('returnTo')) {
      cleanUrl()
      cleanQueryParams()
      history.push('/onboarding')
      return
    }

    handleReturnTo()
  }

  function loginFailureCallback(error) {
    setLoading(false)
    clearErrorTimeout()

    if (!error) {
      setError('Cannot currently login to Google, please try again later.')
    } else {
      setError(JSON.stringify(error))
    }
    postErrorEvent('Login Error', 'loginError', error)
  }

  function handleLogout(forced, isExtension) {
    try {
      logout(forced, isExtension)
    } catch (error) {
      postErrorEvent('Logout Error', 'logoutError', error)
    }
  }

  function clearErrorTimeout() {
    clearTimeout(errorTimeout.current)
    errorTimeout.current = null
  }

  function handleReturnTo() {
    const stateParams = new URLSearchParams(queryParams.state)
    const returnTo = stateParams.get('returnTo')
    const returnIfAdult = stateParams.get('returnIfAdult')
    const isAMinor = Boolean(stateParams.get('isMinor'))
    const returnToUrlObject = new URL(returnTo, window.location.origin)

    if (
      !RETURN_TO_WHITELIST.includes(
        returnToUrlObject.pathname.replace('/', '')
      ) &&
      !returnTo.startsWith('/m/')
    )
      return

    if (returnIfAdult && isAMinor) return

    history.push(returnTo)
  }

  function cleanUrl() {
    try {
      window.history.replaceState(
        { page: 1 },
        'Mote Login',
        window.location.protocol + '//' + window.location.host + '/login'
      )
    } catch (e) {}
  }

  function startOauth() {
    track({
      variables: { event: 'Authentication: Start Login', withScopes: 0 }
    })
    const extras = { legals: LEGALS_VERSION }
    if (queryParams.returnTo) {
      extras.returnTo = queryParams.returnTo
    }
    if (queryParams.returnIfAdult) {
      extras.returnIfAdult = queryParams.returnIfAdult
    }
    if (referrerCode) {
      extras.referrerCode = referrerCode
    }
    window.location.href = getOAuthURL(extras)
  }

  if (error) {
    return (
      <AuthLayout>
        <LoginError error={error} />
      </AuthLayout>
    )
  }

  if (loading) {
    return (
      <AuthLayout>
        <Spinner radius={65} />
      </AuthLayout>
    )
  }

  return <AuthForm data={screenData} />
}
